import React from 'react';
import { Empty } from 'antd';
import { useAzureMLPipeline } from '../../../../core/modules/pipelines/hooks';
import { AzureMLDetailSkeleton } from '../skeletons';
import { AzureMLKey } from '../../../../core/modules/pipelines/pipelines.model';
import HeroHeader from '../HeroHeader';
import HorizontalStages from '../HorizontalStages';
import RunHistoryTable from '../RunHistoryTable';
import StatStrip from '../StatStrip';
import { Section, StudioLinkPara, SubHeading } from './detail-shared';

interface IAzureMLDetailProps {
  pipelineKey: AzureMLKey;
  title: string;
  schedule: string;
  description?: string | null;
  onClose?: () => void;
}

const AzureMLDetail: React.FC<IAzureMLDetailProps> = ({
  pipelineKey, title, schedule, description, onClose
}) => {
  const { data, isLoading, isError } = useAzureMLPipeline(pipelineKey);

  if (isLoading) {
    return <AzureMLDetailSkeleton />;
  }

  if (isError || !data) {
    return <Empty description='Failed to load Azure ML pipeline status' />;
  }

  const detail = data.data;
  const { current } = detail;

  return (
    <>
      <HeroHeader
        title={title}
        schedule={schedule}
        current={current}
        cacheStatus={data.cache}
        fetchedAt={data.fetched_at}
        description={description}
        onClose={onClose}
      />
      <StatStrip
        current={current}
        runs={detail.runs}
        showDuration={false}
        showTrend={false}
      />

      <Section>
        <SubHeading>Stages</SubHeading>
        {current ? (
          <>
            <HorizontalStages stages={current.stages} pipelineStartedAt={current.startedAt} />
            {current.studioUrl && (
              <StudioLinkPara>
                <a href={current.studioUrl} target='_blank' rel='noreferrer'>
                  Open in Azure ML Studio →
                </a>
              </StudioLinkPara>
            )}
          </>
        ) : (
          <Empty description='No recent runs' />
        )}
      </Section>

      <Section>
        <SubHeading>Recent runs</SubHeading>
        <RunHistoryTable
          runs={detail.runs}
          showDurationColumn={false}
          showErrorColumn={false}
        />
      </Section>
    </>
  );
};

export default AzureMLDetail;
